import type { GoogleFontImport } from './googleFonts';

export const CAPTIONS_STYLE_PRESET_NAMES = Object.seal([
  'Impactful',
  'Dynamic',
  'Classic',
  'Popping',
  'Professional',
] as const);

export type CaptionsStylePresetName = (typeof CAPTIONS_STYLE_PRESET_NAMES)[number];

type CssValue = string | number;

export type CaptionsStyleDefinition = {
  grouping?: {
    words?: number;
    characters?: number;
    minDurationInSeconds?: number;
  } | null;

  align?: 'left' | 'center' | 'right';

  animation?: 'pop' | 'fade-in' | 'fade-in-out' | 'none';

  padding?: CssValue;

  fontSize?: number;
  fontWeight?: CssValue;
  fontFamily?: string;
  lineHeight?: CssValue;
  color?: string;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  textStroke?: {
    color?: string;
    width?: number;
  } | null;
  textShadow?: {
    color?: string;
    x?: number;
    y?: number;
    blur?: number;
    strength?: number;
  } | null;

  box?: {
    type?: 'group' | 'line' | 'sliding';
    backgroundColor?: string;
    padding?: CssValue;
    borderRadius?: number;
  } | null;

  spokenWord?: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    padding?: CssValue;
    margin?: CssValue;
    borderRadius?: number;
  } | null;

  currentWord?: {
    fontSize?: number;
    color?: string;
    backgroundColor?: string;
    padding?: CssValue;
    margin?: CssValue;
    borderRadius?: number;
  } | null;
};

const Impactful = Object.seal({
  grouping: { words: 8, characters: 40 },
  fontSize: 64,
  fontWeight: 900,
  fontFamily: 'Montserrat, sans-serif',
  lineHeight: 1.25,
  color: 'white',
  textAlign: 'center',
  textTransform: 'uppercase',
  textStroke: {
    color: 'black',
    width: 8,
  },
  textShadow: {
    color: 'black',
    x: 4,
    y: 4,
    blur: 12,
    strength: 8,
  },

  currentWord: {
    fontSize: 64,
    color: 'mediumpurple',
  },
}) satisfies CaptionsStyleDefinition;

const Dynamic = Object.seal({
  grouping: { words: 8, characters: 40 },

  fontSize: 80,
  fontWeight: 900,
  fontFamily: 'Roboto Condensed, sans-serif',
  lineHeight: 1.5,
  color: 'white',
  textAlign: 'center',
  textStroke: {
    color: 'black',
    width: 4,
  },
  textShadow: {
    color: 'black',
    x: 5,
    y: 5,
    blur: 30,
    strength: 1,
  },

  currentWord: {
    backgroundColor: 'mediumpurple',
    padding: 16,
    margin: -16,
    borderRadius: 16,
  },
}) satisfies CaptionsStyleDefinition;

const Classic = Object.seal({
  grouping: { words: 16, characters: 70 },
  fontSize: 48,
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
  lineHeight: 2,
  color: 'grey',

  box: {
    type: 'line',
    backgroundColor: 'black',
    padding: 14,
    borderRadius: 10,
  },

  spokenWord: {
    color: 'white',
  },

  currentWord: {
    color: 'white',
  },
}) satisfies CaptionsStyleDefinition;

const Professional = Object.seal({
  grouping: { words: 20, characters: 75 },
  fontSize: 48,
  fontWeight: 600,
  fontFamily: 'Open Sans, sans-serif',
  lineHeight: 1.75,
  color: 'white',

  box: {
    type: 'group',
    backgroundColor: 'black',
    padding: 20,
    borderRadius: 12,
  },
}) satisfies CaptionsStyleDefinition;

const Popping = Object.seal({
  grouping: { words: 1, minDurationInSeconds: 0.25 },
  fontSize: 120,
  fontWeight: 800,
  fontFamily: 'Poppins, sans-serif',
  lineHeight: 1.25,
  color: 'white',
  textAlign: 'center',
  textStroke: {
    color: 'black',
    width: 16,
  },

  animation: 'pop',
}) satisfies CaptionsStyleDefinition;

export const mergeCaptionsStyle = (preset?: CaptionsStylePresetName, custom?: CaptionsStyleDefinition | null) => {
  const base = CAPTIONS_STYLE_PRESETS[preset ?? DEFAULT_CAPTIONS_STYLE_PRESET_NAME];

  if (!custom) {
    return base;
  }

  return {
    ...base,
    ...Object.fromEntries(
      Object.entries(custom)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => (v === null ? [k, undefined] : [k, v]))
    ),
    ...(
      ['grouping', 'textStroke', 'textShadow', 'box', 'spokenWord', 'currentWord'] as const
    ).reduce<CaptionsStyleDefinition>((sub, key) => {
      if (custom[key] === null) {
        return sub;
      }

      if (!custom[key] && !base[key]) {
        return sub;
      }

      return {
        ...sub,
        [key]: {
          ...base[key],
          ...custom[key],
        },
      };
    }, {}),
  };
};

export const CAPTIONS_STYLE_PRESETS: Readonly<Record<CaptionsStylePresetName, Readonly<CaptionsStyleDefinition>>> =
  Object.seal({
    Impactful,
    Dynamic,
    Classic,
    Popping,
    Professional,
  });

export const DEFAULT_CAPTIONS_STYLE_PRESET_NAME: CaptionsStylePresetName = 'Impactful';

// What each preset looks like, in English, for assistants choosing a caption style. A caption style row may
// carry its own context on top; how the two combine is not decided here, and nothing reads either yet.
export const CAPTIONS_STYLE_PRESET_LLM_CONTEXT: Readonly<Record<CaptionsStylePresetName, string>> = Object.seal({
  Impactful:
    'Uppercase heavy sans in white, very large, wrapped in a thick black outline and a hard drop shadow. Up to eight words at a time; the word being spoken turns purple.',
  Dynamic:
    'Condensed heavy sans in white, very large, with a thin black outline and a wide soft shadow. Up to eight words at a time; the word being spoken sits on a rounded purple highlight.',
  Classic:
    'Medium-weight sans in grey on a black rounded bar behind each line, moderate size, up to sixteen words at a time. Words brighten to white as they are spoken.',
  Professional:
    'Medium-weight sans in white on a single black rounded box behind the whole group, moderate size, up to twenty words at a time. No per-word emphasis; the quietest of the presets.',
  Popping:
    'One word at a time, enormous, in a heavy geometric sans with a very thick black outline. Each word pops in and is held briefly.',
});

// A selectable Google font; `value` is the CSS stack matching the preset fontFamily (dropdown value + stored value + lookup key).
export type CaptionFont = { value: string; googleImport: string };

// Selectable Google fonts for caption styles; owned separately from SUBTITLE_FONTS.
export const CAPTION_FONTS: readonly CaptionFont[] = [
  { value: 'Montserrat, sans-serif', googleImport: 'Montserrat:ital,wght@0,100..900;1,100..900' },
  { value: 'Roboto Condensed, sans-serif', googleImport: 'Roboto+Condensed:wght@100..900' },
  { value: 'Inter, sans-serif', googleImport: 'Inter:opsz,wght@14..32,100..900' },
  { value: 'Open Sans, sans-serif', googleImport: 'Open+Sans:wdth,wght@75..100,300..800' },
  { value: 'Poppins, sans-serif', googleImport: 'Poppins:wght@100;200;300;400;500;600;700;800;900' },
] as const;

// Selected font if it is a known CAPTION_FONTS entry, else empty (template fonts load via the Fonts component).
export const resolveCaptionImportFonts = (fontFamily?: string): GoogleFontImport[] => {
  const selected = CAPTION_FONTS.find((font) => font.value === fontFamily);
  return selected ? [{ googleImport: selected.googleImport }] : [];
};
